import {
	buildAccessPointNavigationState,
	isAccessPointNavigationReady,
} from './navigation';
import {
	openApAlarmsSection,
	openApRadioSettingsSection,
} from '../apSections/adapter';
import {resolveAccessPoints} from './resolver';
import {readAccessPointStatusCounts} from './statistics';
import {serializeAccessPointContext} from './context';
import {
	applyAccessPointFilter,
	focusAccessPoint,
	openAccessPointBulkSection,
	openAccessPointSection,
} from './adapter';
import {
	openAccessPointConfigureModal,
	openAccessPointRadioModal,
	openAccessPointCreateModal,
	openAccessPointDeleteModal,
	openAccessPointMoveModal,
	openAccessPointRebootModal,
} from './modal';
import {openAccessPointEditExperience} from './experience';
import {
	mapAccessPointEditPrefill,
	mapAccessPointPrefill,
} from './prefill';
import {
	highlightExistingAction,
	highlightFirstExistingControl,
} from '../../core/actionHighlighter';

const accessPointPlugin = {
	id: 'access_point',
	version: 1,
	commandVersion: 1,
	resource: 'ap',
	operations: [
		'options',
		'list',
		'search',
		'open',
		'create',
		'edit',
		'configure',
		'radio',
		// The two accordions on the dashboard this resource owns. Alarms
		// the way switch.alarm_list already works, and radio_settings as
		// the read view of what 'radio' opens for editing.
		'alarm_list',
		'radio_panel',
		'move',
		'reboot',
		'delete',
		'blink',
		'upgrade',
		'snapshot',
		'remote_access',
		'factory_reset',
		'change_password',
		'bulk_delete',
		// The bulk screens on the Maintenance tab. Reboot, Reset and
		// Update share one screen there, with the task chosen at the top.
		'bulk_add',
		'bulk_reboot',
		'bulk_reset',
		'bulk_update',
		'stats',
		'status',
		'status_summary',
		'download_inventory',
		'download_report',
	],
	dependencies: {
		plugins: ['site', 'canvas.navigation'],
		resolvers: ['site'],
		destinations: [
			'home.dashboard',
			'site.dashboard',
			// Bulk work lives on the Maintenance tab.
			'maintenance.dashboard',
		],
	},
	destinations: {
		'ap.dashboard': {
			path: '/accessPointDashboard',
			buildState: buildAccessPointNavigationState,
		},
	},
	completionVerifiers: {
		'navigate:ap.dashboard': isAccessPointNavigationReady,
	},
	resolvers: {
		ap: resolveAccessPoints,
	},
	sections: {
		'ap.alarms': openApAlarmsSection,
		'ap.radio_settings': openApRadioSettingsSection,
		// Bulk work is a fourth surface: it lives on the Maintenance tab, not
		// beside the inventory it acts on, and one screen covers Reboot, Reset
		// and Update - which is chosen at the top of it.
		'maintenance.bulk_add_access_points': () =>
			openAccessPointBulkSection('maintenance.bulk_add_access_points'),
		'maintenance.bulk_ap_actions': () =>
			openAccessPointBulkSection('maintenance.bulk_ap_actions'),
		'home.access_points': async (command) => {
			const evidence = await openAccessPointSection('home.access_points');
			if (command.params?.action_target === 'download_inventory' &&
				!highlightExistingAction(['Download'], [], {
					activate: command.params?.action_activation === 'auto',
					commandId: command.command_id,
				})) {
				throw new Error('ap_download_unavailable');
			}
			if (command.params?.action_target === 'bulk_select_delete' &&
				!highlightFirstExistingControl([
					'[role="grid"] [role="checkbox"]',
					'table thead input[type="checkbox"]',
				])) throw new Error('ap_bulk_selection_unavailable');
			return evidence;
		},
		'site.access_points': async (command) => {
			const evidence = await openAccessPointSection('site.access_points');
			if (command.params?.action_target === 'bulk_select_delete' &&
				!highlightFirstExistingControl([
					'[role="grid"] [role="checkbox"]',
					'table thead input[type="checkbox"]',
				])) throw new Error('ap_bulk_selection_unavailable');
			return evidence;
		},
	},
	statistics: {
		ap_status: readAccessPointStatusCounts,
	},
	filters: {
		ap: (command) => applyAccessPointFilter(
			command.params?.query,
			command.params?.status,
		),
	},
	focusHandlers: {
		ap: (command) => focusAccessPoint(command.params?.entity_id),
	},
	modals: {
		'ap.create': openAccessPointCreateModal,
		'ap.configure': openAccessPointConfigureModal,
		'ap.radio': openAccessPointRadioModal,
		'ap.move': openAccessPointMoveModal,
		'ap.reboot': openAccessPointRebootModal,
		'ap.delete': openAccessPointDeleteModal,
	},
	experiences: {
		'ap.edit': openAccessPointEditExperience,
	},
	prefillMappers: {
		'ap.create': mapAccessPointPrefill,
		'ap.edit': mapAccessPointEditPrefill,
		'ap.move': mapAccessPointPrefill,
	},
	contextSerializers: {
		accessPoint: serializeAccessPointContext,
	},
};

export default accessPointPlugin;
